// Section 10 Tools panel (stream M3a): tools on hand, effective tools after durability, demand
// from the draft's assignments and the shortfall, the allocation policy, manual per-person pins,
// and per worker whether they had a tool last season and the output difference it made.
import {
  catalog,
  toolStock,
  type Decision,
  type GameState,
  type JobId,
  type PersonId,
  type ToolAllocationOption,
} from '../sim/api';

export interface ToolWorkerRow {
  id: PersonId;
  name: string;
  job: JobId;
  /** Tool multiplier of the job they will work next season. */
  toolFactor: number;
  /** Wants a tool next season (working at a tool job under the draft). */
  wants: boolean;
  /** Last season: had a tool (true/false), or null if they were not at a tool job. */
  hadTool: boolean | null;
  /** Last season's output in the job's unit, if they worked. */
  output: number | null;
  /**
   * Output the tool made last season (had one: output - output / factor), or would have made
   * (had none: output x (factor - 1)).
   */
  toolDifference: number | null;
  /** Manual pin: true give, false withhold, null none (draft over standing). */
  pinned: boolean | null;
}

export interface ToolsVM {
  amount: number;
  onHand: number;
  durability: number;
  effective: number;
  demand: number;
  shortfall: number;
  policy: ToolAllocationOption;
  options: ToolAllocationOption[];
  manual: boolean;
  seasonsWithoutToolmaker: number;
  warnAfter: number;
  decayAfter: number;
  /** Last season's `tools` entry. */
  lastSeason: { made: number; repaired: number; worn: number } | null;
  workers: ToolWorkerRow[];
}

const POLICIES: readonly ToolAllocationOption[] = [
  'farmers-first',
  'highest-output',
  'builders-first',
  'even-rotation',
  'manual',
];

export function toolsVM(game: GameState, draft: Decision | null): ToolsVM {
  const cat = catalog();
  const factor = (job: JobId): number | null =>
    cat.jobs.find((j) => j.id === job)?.toolFactor ?? null;
  const stock = toolStock(game, draft);
  const wanting = new Set<string>(stock.wanting);
  const last = game.history[game.history.length - 1] ?? null;
  const entry = last?.entries.find((e) => e.kind === 'tools');
  const policy = draft?.policies.toolAllocation ?? game.policies.toolAllocation;
  const ids = new Set<PersonId>([
    ...(stock.wanting as PersonId[]),
    ...(Object.keys(last?.toolUse ?? {}) as PersonId[]),
  ]);
  const workers: ToolWorkerRow[] = [];
  for (const id of [...ids].sort()) {
    const p = game.people[id];
    if (p === undefined) continue;
    const job = draft?.assignments[id] ?? p.job;
    const hadTool = last?.toolUse[id] ?? null;
    const out = last?.outputs[id];
    const lastFactor = out === undefined ? null : factor(out.jobId);
    let toolDifference: number | null = null;
    if (out !== undefined && lastFactor !== null && hadTool !== null)
      toolDifference = hadTool
        ? out.amount - out.amount / lastFactor
        : out.amount * (lastFactor - 1);
    const draftPin = draft?.toolOverrides[id];
    workers.push({
      id,
      name: p.name,
      job,
      toolFactor: factor(job) ?? 1,
      wants: wanting.has(id),
      hadTool,
      output: out?.amount ?? null,
      toolDifference,
      pinned: draftPin ?? game.toolOverrides[id] ?? null,
    });
  }
  return {
    amount: stock.amount,
    onHand: stock.onHand,
    durability: stock.durability,
    effective: stock.effective,
    demand: stock.demand,
    shortfall: stock.shortfall,
    policy,
    options: [
      ...((cat.policies.find((p) => p.id === 'toolAllocation')?.options ??
        POLICIES) as ToolAllocationOption[]),
    ],
    manual: policy === 'manual',
    seasonsWithoutToolmaker: stock.seasonsWithoutToolmaker,
    warnAfter: cat.tools.noToolmakerWarnSeasons,
    decayAfter: cat.tools.noToolmakerDecaySeasons,
    lastSeason:
      entry?.kind === 'tools'
        ? { made: entry.made, repaired: entry.repaired, worn: entry.worn }
        : null,
    workers,
  };
}
